import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Button,
    TablePagination,
    Chip,
    Snackbar,
    Alert,
    Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import QrCodeIcon from '@mui/icons-material/QrCode';
import BrushIcon from '@mui/icons-material/Brush';
import BarChartIcon from '@mui/icons-material/BarChart';
import api from '../../../services/api';
import QRCodeModal from './QRCodeModal';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import { useNavigate } from 'react-router-dom';

interface Coupon {
    _id: string;
    code: string;
    title: string;
    startDate: string;
    endDate: string;
    validFrom: string;
    validUntil: string;
    isActive: boolean;
    usageLimit: number;
    usageCount: number;
    createdAt: string;
    isDraft: boolean;
    scanStats: {
        deviceBreakdown: {
            mobile: number;
            tablet: number;
            desktop: number;
        };
        totalScans: number;
        uniqueScans: number;
    };
}

interface SnackbarMessage {
    message: string;
    severity: 'success' | 'error';
}

const CouponList: React.FC = () => {
    const navigate = useNavigate();
    const [coupons, setCoupons] = useState<Coupon[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCoupons, setTotalCoupons] = useState(0);
    const [snackbar, setSnackbar] = useState<SnackbarMessage | null>(null);
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [selectedCouponId, setSelectedCouponId] = useState<string>('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [couponToDelete, setCouponToDelete] = useState<Coupon | null>(null);

    const showMessage = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(null);
    };

    const formatDateRange = (start: string, end: string) => {
        const formatShortDate = (date: string) => {
            return new Intl.DateTimeFormat('en-US', {
                year: '2-digit',
                month: 'numeric',
                day: 'numeric'
            }).format(new Date(date));
        };
        
        return (
            <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatShortDate(start)} - {formatShortDate(end)}
            </Typography>
        );
    };

    const fetchCoupons = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/coupons', {
                params: {
                    page: page + 1,
                    limit: rowsPerPage
                }
            });
            setCoupons(response.data.data.coupons);
            setTotalCoupons(response.data.pagination.total);
        } catch (error) {
            console.error('Error fetching coupons:', error);
            showMessage('Error fetching coupons', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        console.log('CouponList component mounted');
        fetchCoupons();
    }, []);

    useEffect(() => {
        fetchCoupons();
    }, [page, rowsPerPage]);

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleToggleCouponStatus = async (coupon: Coupon) => {
        try {
            await api.patch(`/admin/coupons/${coupon._id}/status`, {
                isActive: !coupon.isActive
            });
            showMessage('Coupon status updated successfully', 'success');
            fetchCoupons();
        } catch (error) {
            console.error('Error updating coupon status:', error);
            showMessage('Error updating coupon status', 'error');
        }
    };

    const handleDeleteClick = (coupon: Coupon) => {
        setCouponToDelete(coupon);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!couponToDelete) return;

        try {
            await api.delete(`/admin/coupons/${couponToDelete._id}`);
            showMessage('Coupon deleted successfully', 'success');
            fetchCoupons();
        } catch (error) {
            console.error('Error deleting coupon:', error);
            showMessage('Error deleting coupon', 'error');
        }
    };

    const handleViewQR = (couponId: string) => {
        setSelectedCouponId(couponId);
        setQrModalOpen(true);
    };

    if (loading) {
        return <Typography>Loading...</Typography>;
    }

    return (
        <Box sx={{ 
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 3 
            }}>
                <Typography variant="h5">Coupon Management</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/admin/coupons/new')}
                >
                    Create New Coupon
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Code</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Sign Up Period</TableCell>
                            <TableCell>Valid Period</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {coupons.map((coupon) => (
                            <TableRow key={coupon._id}>
                                <TableCell>{coupon.code}</TableCell>
                                <TableCell>{coupon.title}</TableCell>
                                <TableCell>
                                    {formatDateRange(coupon.startDate, coupon.endDate)}
                                </TableCell>
                                <TableCell>
                                    {formatDateRange(coupon.validFrom, coupon.validUntil)}
                                </TableCell>
                                <TableCell>
                                    <Chip 
                                        label={coupon.isActive ? "Active" : "Inactive"}
                                        color={coupon.isActive ? "success" : "default"}
                                        size="small"
                                        onClick={() => handleToggleCouponStatus(coupon)}
                                        sx={{ minWidth: 75 }}
                                    />
                                </TableCell>
                                <TableCell>
                                    {new Date(coupon.createdAt).toLocaleDateString()}
                                </TableCell>
                                <TableCell align="right">
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Tooltip title="View QR Code">
                                            <IconButton
                                                onClick={() => handleViewQR(coupon._id)}
                                                size="small"
                                            >
                                                <QrCodeIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit Coupon">
                                            <IconButton 
                                                onClick={() => navigate(`/admin/coupons/${coupon._id}/edit`)}
                                                size="small"
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Design Coupon">
                                            <IconButton
                                                onClick={() => navigate(`/admin/coupons/${coupon._id}/design`)}
                                                size="small"
                                            >
                                                <BrushIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="View Analytics">
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/admin/coupons/${coupon._id}/analytics`)}
                                            >
                                                <BarChartIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete Coupon">
                                            <IconButton
                                                onClick={() => handleDeleteClick(coupon)}
                                                size="small"
                                                color="error"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalCoupons}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            <QRCodeModal
                open={qrModalOpen}
                onClose={() => setQrModalOpen(false)}
                couponId={selectedCouponId}
            />

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleDeleteConfirm}
                couponCode={couponToDelete?.code || ''}
            />

            {snackbar && (
                <Snackbar 
                    open={true} 
                    autoHideDuration={6000} 
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert 
                        onClose={handleCloseSnackbar} 
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            )}
        </Box>
    );
};

export default CouponList;
